// Payment Tracking Types

export type PaymentMethod =
  | 'BANK_TRANSFER'
  | 'CASH'
  | 'CHEQUE'
  | 'UPI'
  | 'CREDIT_CARD'
  | 'DEBIT_CARD'
  | 'OTHER';

export type PaymentStatus = 'NO_PAYMENT' | 'PARTIAL' | 'FULL';

export type PaymentRecordStatus =
  | 'PENDING'
  | 'RECEIVED'
  | 'VERIFIED'
  | 'REFUNDED';

export interface Payment {
  id: string;
  leadId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  invoiceNumber?: string;
  receiptNumber?: string;
  transactionId?: string;
  chequeNumber?: string;
  bankName?: string;
  milestoneId?: string;
  milestone?: PaymentMilestone;
  status: PaymentRecordStatus;
  verifiedAt?: string;
  verifiedBy?: {
    id: string;
    name: string;
    email: string;
  };
  notes?: string;
  recordedBy: {
    id: string;
    name: string;
    email: string;
  };
  isRefund: boolean;
  refundReason?: string;
  originalPaymentId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentMilestone {
  id: string;
  leadId: string;
  title: string;
  description?: string;
  amount: number;
  dueDate: string;
  percentage?: number;
  isCompleted: boolean;
  completedDate?: string;
  sortOrder: number;
  payments?: Payment[];
  createdAt: string;
  updatedAt: string;
}

export interface LeadPaymentSummary {
  id: string;
  name: string;
  dealValue: number;
  totalPaid: number;
  paymentStatus: PaymentStatus;
  stage: string;
}

export interface PaymentSummary {
  totalPaid: number;
  dealValue: number;
  pendingAmount: number;
  paymentStatus: PaymentStatus;
  paymentsCount: number;
}

export interface LeadPaymentsResponse {
  lead: LeadPaymentSummary;
  payments: Payment[];
  summary: PaymentSummary;
}

export type AgingBucket = '0-7' | '8-30' | '31-60' | '61-90' | '91+' | 'paid';

export interface ArAgingBucketEntry {
  count: number;
  amount: number;
}

export interface ArAgingBuckets {
  '0-7': ArAgingBucketEntry;
  '8-30': ArAgingBucketEntry;
  '31-60': ArAgingBucketEntry;
  '61-90': ArAgingBucketEntry;
  '91+': ArAgingBucketEntry;
  paid?: ArAgingBucketEntry;
}

export interface ArByExecutiveEntry {
  userId: string | null;
  userName: string;
  count: number;
  amount: number;
  oldestDays: number;
}

export interface FinanceDashboardSummary {
  totalWonDeals: number;
  totalDealValue: number;
  totalReceived: number;
  totalPending: number;
  noPaymentCount: number;
  partialPaymentCount: number;
  fullPaymentCount: number;
  // AR aging (optional — filled in by new backend)
  collectionRate?: number;
  totalOutstanding?: number;
  avgDaysToPay?: number;
  atRisk60Plus?: ArAgingBucketEntry;
  arAging?: ArAgingBuckets;
  arByExecutive?: ArByExecutiveEntry[];
}

export interface SubscriptionSummary {
  planName?: string;
  planPrice?: number;
  billingCycle?: string;
  status?: string;
}

export interface WonDealWithPayments {
  id: string;
  name: string;
  company: string;
  dealValue: number;
  totalPaid: number;
  pendingAmount: number;
  paymentStatus: PaymentStatus;
  nextPaymentDate?: string;
  nextPaymentAmount?: number;
  wonDate: string;
  assignedTo?: {
    id: string;
    name: string;
    email: string;
  } | null;
  paymentsCount: number;
  milestonesCount: number;
  // New AR-aging / contact fields (optional — filled in by new backend)
  displayId?: string;
  daysPending?: number;
  agingBucket?: AgingBucket;
  wonAt?: string | null;
  subscription?: SubscriptionSummary | null;
  phoneNumber?: string | null;
  email?: string | null;
  agingPriorityScore?: number;
}

export interface UpcomingPayment {
  id: string;
  title: string;
  amount: number;
  dueDate: string;
  leadId: string;
  leadName: string;
  company: string;
  assignedTo?: string;
}

export interface OverduePayment extends UpcomingPayment {
  daysOverdue: number;
}

export interface FinanceDashboardData {
  summary: FinanceDashboardSummary;
  deals: WonDealWithPayments[];
  upcomingPayments: UpcomingPayment[];
  overduePayments: OverduePayment[];
}

export interface CreatePaymentData {
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  invoiceNumber?: string;
  receiptNumber?: string;
  transactionId?: string;
  chequeNumber?: string;
  bankName?: string;
  milestoneId?: string;
  notes?: string;
}

export interface UpdatePaymentData {
  amount?: number;
  paymentDate?: string;
  paymentMethod?: PaymentMethod;
  invoiceNumber?: string;
  receiptNumber?: string;
  transactionId?: string;
  chequeNumber?: string;
  bankName?: string;
  milestoneId?: string;
  notes?: string;
}

export interface CreateMilestoneData {
  title: string;
  description?: string;
  amount: number;
  dueDate: string;
  percentage?: number;
}

export interface RefundData {
  amount?: number;
  reason: string;
}

export interface FinanceFilters {
  paymentStatus?: PaymentStatus;
  assignedTo?: string;
  dateFrom?: string;
  dateTo?: string;
}

// Payment method display helpers
export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  BANK_TRANSFER: 'Bank Transfer',
  CASH: 'Cash',
  CHEQUE: 'Cheque',
  UPI: 'UPI',
  CREDIT_CARD: 'Credit Card',
  DEBIT_CARD: 'Debit Card',
  OTHER: 'Other'
};

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, { bg: string; text: string; dot: string }> = {
  NO_PAYMENT: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
  PARTIAL: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
  FULL: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' }
};

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  NO_PAYMENT: 'No Payment',
  PARTIAL: 'Partial',
  FULL: 'Fully Paid'
};

// ---------------------------------------------------------------------------
// Monthly Trend — Booked vs Collected, 6-month rolling window
// ---------------------------------------------------------------------------

export interface MonthlyTrendPoint {
  month: string;        // e.g. '2025-11'
  label: string;        // e.g. 'Nov 2025'
  booked: number;       // sum of WON deal values in IST month
  collected: number;    // sum of RECEIVED/VERIFIED payments in IST month
  rate: number;         // percent = collected / booked * 100 (0 when booked is 0)
}

export interface MonthlyTrendResponse {
  success: boolean;
  data: {
    months: MonthlyTrendPoint[];
  };
}
